import './productDetailCarousel.scss';

import React, { forwardRef, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CarouselContext } from 'pure-react-carousel';

import { Grid, Carousel } from '../base';
import CloudinaryPicture from './cloudinaryPicture';
import Icon, { ICON_TYPE } from '../icon';
import isVideoImage from 'utils/isVideoImage';

// Auto-advance must wait for a video to finish instead of firing on the fixed interval. While a
// video slide is active this pauses the carousel's interval timer, (re)starts that video, and
// advances when it ends; image slides keep the built-in interval.
const VideoAwareAutoplay = ({ images, videoRefs }) => {
  const carousel = useContext(CarouselContext);

  useEffect(() => {
    if (!carousel) return undefined;
    let current = -1;
    let activeVideo = null;
    let handleEnded = null;

    const detach = () => {
      if (activeVideo && handleEnded) activeVideo.removeEventListener('ended', handleEnded);
      activeVideo = null;
      handleEnded = null;
    };

    const sync = () => {
      const index = carousel.state.currentSlide;
      if (index === current) return;
      current = index;
      detach();

      const onVideo = isVideoImage(images[index]);
      // Suspend the interval timer while a video plays; let it run for images.
      if (carousel.state.isPlaying === onVideo) {
        carousel.setStoreState({ isPlaying: !onVideo });
      }
      if (!onVideo || images.length < 2) return;

      const video = videoRefs.current[index];
      if (!video) return;
      // Restart so a revisited slide replays from the beginning, then advance once it ends.
      video.currentTime = 0;
      // Ignore play() rejections (e.g. autoplay policy) — the poster stays visible.
      video.play().catch(() => undefined);
      handleEnded = () => {
        carousel.setStoreState({ currentSlide: (index + 1) % images.length, isPlaying: true });
      };
      activeVideo = video;
      video.addEventListener('ended', handleEnded);
    };

    sync();
    carousel.subscribe(sync);
    return () => {
      carousel.unsubscribe(sync);
      detach();
    };
  }, [carousel, images, videoRefs]);

  return null;
};

const ProductDetailCarousel = forwardRef((props, ref) => {
  const bigClassName = classNames({
    "ratio-1x1": true,
    rounded: props.rounded,
  });

  const thumbClassName = classNames({
    'w-100': true,
    "ratio-1x1": true,
    rounded: props.rounded,
  });

  // Big-slide <video> nodes, keyed by slide index, for VideoAwareAutoplay to control.
  const videoRefs = useRef({});

  return (
    <Carousel
      ref={ref}
      className="carousel-product-images"
      totalSlides={props.images.length}
      // Don't start the interval timer if the first slide is a video — the controller resumes it
      // once the video ends.
      isPlaying={!isVideoImage(props.images[0])}
      infinite
      visibleSlides={1}
    >
      <VideoAwareAutoplay images={props.images} videoRefs={videoRefs} />

      <Carousel.Slider className="mb-3">
        {props.images.map((image, index) => (
          <Carousel.Slide key={image.key} ref={ref} index={index}>
            <CloudinaryPicture
              prefix={props.prefix}
              imageKey={image.key}
              assetType={image.type}
              className={bigClassName}
              type="big"
              // Multi-slide galleries play the video once and advance on end; a lone video loops.
              loop={props.images.length <= 1}
              videoRef={isVideoImage(image) ? el => { videoRefs.current[index] = el; } : undefined}
            />
          </Carousel.Slide>
        ))}
      </Carousel.Slider>

      <Grid.Row className="link-thumb-row">
        {props.images.map((image, index) => (
          <Grid.Col key={image.key} className="col link-thumb-col">
            <Carousel.Dot slide={index}>
              <CloudinaryPicture
                prefix={props.prefix}
                imageKey={image.key}
                assetType={image.type}
                className={thumbClassName}
                type="small"
                alt=""
              />
              {isVideoImage(image) && (
                <div className="video-thumbnail text-warning position-absolute w-100 h-100 d-flex justify-content-center align-items-center" style={{ left: 0, top: 0, pointerEvents: 'none' }}>
                  <div className='position-absolute' style={{ background: "white", width: 20, height: 20 }}></div>
                  <Icon type={ICON_TYPE.YOUTUBE} size={48} className='position-absolute' />
                </div>
              )}
            </Carousel.Dot>
          </Grid.Col>
        ))}
      </Grid.Row>
    </Carousel>
  );
});

ProductDetailCarousel.propTypes = {
  prefix: PropTypes.string,
  images: PropTypes.array,
  rounded: PropTypes.bool,
};

ProductDetailCarousel.defaultProps = {
  prefix: 'products/',
  images: [],
  rounded: false,
};

export default ProductDetailCarousel;
